/**
 * Dark Mode Toggle Component
 *
 * Toggle elegante para alternar entre modo claro e escuro.
 * Design inspirado em toggles modernos com animacao suave.
 * Usa icones Bootstrap Icons (flat, single color).
 *
 * @since 2025-12-18
 * @epic INIT-0045
 */

import React from 'react';
import { useTheme } from '@/theme';

interface DarkModeToggleProps {
  /** Label do toggle */
  label?: string;
  /** Mostrar label */
  showLabel?: boolean;
  /** Tamanho do toggle */
  size?: 'sm' | 'md' | 'lg';
  /** Classes CSS adicionais */
  className?: string;
}

export function DarkModeToggle({
  label,
  showLabel = true,
  size = 'md',
  className = '',
}: DarkModeToggleProps) {
  const { isDarkMode, toggleDarkMode } = useTheme();

  // Tamanhos do toggle
  const sizes = {
    sm: {
      track: 'w-10 h-5',
      thumb: 'w-4 h-4',
      translate: 'translate-x-5',
      icon: 'text-[10px]',
      trackIcon: 'text-[8px]',
    },
    md: {
      track: 'w-12 h-6',
      thumb: 'w-5 h-5',
      translate: 'translate-x-6',
      icon: 'text-xs',
      trackIcon: 'text-[10px]',
    },
    lg: {
      track: 'w-14 h-7',
      thumb: 'w-6 h-6',
      translate: 'translate-x-7',
      icon: 'text-sm',
      trackIcon: 'text-xs',
    },
  };

  const sizeConfig = sizes[size];

  return (
    <div className={`flex items-center justify-between gap-3 ${className}`}>
      {showLabel && (
        <span className="text-sm text-foreground">
          {label || (isDarkMode ? 'Modo Escuro' : 'Modo Claro')}
        </span>
      )}

      <button
        type="button"
        role="switch"
        aria-checked={isDarkMode}
        aria-label={isDarkMode ? 'Ativar modo claro' : 'Ativar modo escuro'}
        onClick={toggleDarkMode}
        className={`
          relative inline-flex items-center shrink-0
          ${sizeConfig.track}
          rounded-full cursor-pointer
          transition-colors duration-300 ease-in-out
          focus:outline-none focus:ring-2 focus:ring-offset-2
          ${isDarkMode
            ? 'bg-[var(--brand-primary)]'
            : 'bg-gray-300 hover:bg-gray-400'
          }
        `}
        style={{
          '--tw-ring-color': 'var(--brand-primary)',
        } as React.CSSProperties}
      >
        {/* Track icons - icones no fundo do track */}
        <span className="absolute inset-0 flex items-center justify-between px-1.5">
          {/* Sol (visivel quando dark mode ativo - lado esquerdo) */}
          <i
            className={`
              bi bi-sun-fill ${sizeConfig.trackIcon}
              text-gray-200 transition-opacity duration-200
              ${isDarkMode ? 'opacity-100' : 'opacity-0'}
            `}
          />
          {/* Lua (visivel quando light mode ativo - lado direito) */}
          <i
            className={`
              bi bi-moon-fill ${sizeConfig.trackIcon}
              text-muted-foreground transition-opacity duration-200
              ${isDarkMode ? 'opacity-0' : 'opacity-100'}
            `}
          />
        </span>

        {/* Thumb (bolinha) */}
        <span
          className={`
            ${sizeConfig.thumb}
            bg-card rounded-full shadow-md
            transform transition-transform duration-300 ease-in-out
            flex items-center justify-center
            ${isDarkMode ? sizeConfig.translate : 'translate-x-0.5'}
          `}
        >
          {/* Icone dentro do thumb */}
          <i
            className={`
              bi ${isDarkMode ? 'bi-moon-fill text-amber-300' : 'bi-sun-fill text-amber-500'}
              ${sizeConfig.icon}
              transition-colors duration-200
            `}
          />
        </span>
      </button>
    </div>
  );
}

export default DarkModeToggle;
